import { useApp } from '@/App';
import FleetTitle from '@/libs/antd/components/Title';
import { useCreditSimulationList } from '@/libs/fleet-api/hooks/simulation';
import { useDownloadSimulation } from '@/libs/fleet-api/hooks/simulation';
import {
	ArrowLeftOutlined,
	DownloadOutlined,
	EditOutlined,
	EyeOutlined,
} from '@ant-design/icons';
import { Button, Space, Table, Tooltip } from 'antd';
import { useState } from 'react';
import { message } from 'antd';

/**
 * Type for Simulation History Table Row
 */
export interface SimulationHistoryItem {
	key: number;
	noMkl: string;
	noSpf: string;
	noSimulasi: string;
	brandName: string;
	dealerNameName: string;
}

/**
 * FRMSimulationHistoryListPage
 */
const FRMSimulationHistoryListPage = ({ spfId }: { spfId: number }) => {
	const { navigate } = useApp();
	const [pageNum, setPage] = useState(1);
	const pageSize = 10;

	const { data, isLoading, error } = useCreditSimulationList({
		pagination: { pageSize, pageNumber: pageNum },
		...(spfId ? { spfId } : {}),
	});

	const tableData = (data?.items || []).map((item) => ({
		key: item.spfId + item.facilityId,
		spfId: item.spfId,
		facilityId: item.facilityId,
		noMkl: item.mklNo,
		noSpf: item.spfNo,
		noSimulasi: item.simulationNo,
		brandName: item.brandName,
		dealerName: item.dealerName,
		simulationId: item.simulationId,
		isEdit: item.isEdit,
	}));

	const handleEdit = (
		spfId: number,
		facilityId: number,
		simulationId: number,
	) => {
		try {
			navigate('SimulationPage', { spfId, facilityId, simulationId });
		} catch (error) {
			console.error('Navigation error:', error);
		}
	};

	const handleView = (
		spfId: number,
		facilityId: number,
		simulationId: number,
	) => {
		try {
			navigate('SimulationPage', {
				spfId,
				facilityId,
				simulationId,
				viewOnly: true,
				showResult: true,
				// directlyShowCalculation: true, --- IGNORE ---
				onBack: () => {
					navigate('FRMSimulationHistoryListPage', { spfId });
				},
			});
		} catch (error) {
			console.error('Navigation error:', error);
		}
	};

	const downloadMutation = useDownloadSimulation();
	const [downloadingId, setDownloadingId] = useState<number | null>(null);

	const handleDownload = (
		spfId: number,
		facilityId: number,
		simulationId: number,
	) => {
		setDownloadingId(spfId);
		downloadMutation.mutate(
			{ spfId, facilityId, simulationId },
			{
				onSuccess: (data) => {
					console.log(data);
					setDownloadingId(null);
					if (data?.signedUrl) {
						window.open(data.signedUrl, '_blank');
					} else {
						message.error('Gagal mendapatkan link download.');
					}
				},
				onError: () => {
					setDownloadingId(null);
					message.error('Gagal mendownload simulasi.');
				},
			},
		);
	};

	return (
		<Space
			direction="vertical"
			style={{ display: 'flex' }}
			size={'large'}
		>
			<div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
				<div>
					<button
						style={{
							display: 'flex',
							height: 44,
							width: 44,
							fontSize: 24,
							border: 'none',
							alignItems: 'center',
							justifyContent: 'center',
							background: '#1557BC',
							color: 'white',
							cursor: 'pointer',
						}}
						onClick={() => navigate('FRMSimulationListPage')}
					>
						<ArrowLeftOutlined />
					</button>
				</div>
				<div style={{ flex: 1 }}>
					<FleetTitle>View History Simulasi Kredit</FleetTitle>
				</div>
			</div>
			<FleetTitle level={5}>Rincian Simulasi Kredit</FleetTitle>
			<Table
				scroll={{ x: 'max-content' }}
				bordered
				loading={isLoading}
				dataSource={tableData}
				columns={[
					{
						title: 'No',
						width: 1,
						align: 'center',
						render: (_, __, index) => (pageNum - 1) * pageSize + index + 1,
					},
					{ title: 'No.MKL', dataIndex: 'noMkl' },
					{ title: 'No.SPF', dataIndex: 'noSpf' },
					{ title: 'No.Simulasi', dataIndex: 'noSimulasi' },
					{ title: 'Merk/Type', dataIndex: 'brandName' },
					{ title: 'Dealer', dataIndex: 'dealerName' },
					{
						title: 'Edit',
						align: 'center',
						render: (_, record) => (
							<Tooltip title="Edit">
								<Button
									type="link"
									icon={<EditOutlined />}
									onClick={() =>
										handleEdit(
											record.spfId,
											record.facilityId,
											record.simulationId,
										)
									}
									aria-label="Edit"
									disabled={!record.isEdit}
								/>
							</Tooltip>
						),
					},
					{
						title: 'View',
						align: 'center',
						render: (_, record) => (
							<Tooltip title="View">
								<Button
									type="link"
									icon={<EyeOutlined />}
									onClick={() =>
										handleView(
											record.spfId,
											record.facilityId,
											record.simulationId,
										)
									}
									aria-label="View"
								/>
							</Tooltip>
						),
					},
					{
						title: 'Download',
						align: 'center',
						render: (_, record) => (
							<Tooltip title="Download">
								<Button
									type="link"
									icon={<DownloadOutlined />}
									onClick={() =>
										handleDownload(
											record.spfId,
											record.facilityId,
											record.simulationId,
										)
									}
									aria-label="Download"
									loading={
										downloadingId === record.key &&
										downloadMutation.status === 'pending'
									}
								/>
							</Tooltip>
						),
					},
				]}
				pagination={{
					current: pageNum,
					total: data?.totalCount || 0,
					pageSize,
					onChange: setPage,
				}}
				rowKey="key"
			/>
			{error && (
				<div style={{ color: 'red' }}>Gagal memuat data riwayat simulasi.</div>
			)}
		</Space>
	);
};

export default FRMSimulationHistoryListPage;
